import React, { memo, useEffect } from "react";

import Box from "@mui/material/Box";
import SelfUpload from "../SelfUpload";
import { Stack, Typography } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../../redux/store";
import { GetMedia } from "../../../../helpers/xhrHelper";
import MediaCard from "./MediaCard";
import { ProductType } from "../../../product";
import { useTranslation } from "../../../../hooks/useTranslation";
import { Category, Media } from "../../../../types/common.types";

export const sortByCategory = (data: Media[], category?: string) => {
  if (!category) return data;

  return [...data].sort((a, b) => {
    const aHasCategory = a.tags.some((tag) =>
      tag.toLowerCase().includes(category.toLowerCase())
    );
    const bHasCategory = b.tags.some((tag) =>
      tag.toLowerCase().includes(category.toLowerCase())
    );

    return Number(bHasCategory) - Number(aHasCategory);
  });
};

interface ProductPhoto {
  productType?: ProductType;
  productCategory?: Category;
}

/*
 * The backend should return media based on
 * - active frontend category -- filter user tags
 * - active product type -- filter user tags
 * - user jurisdictions
 **/

const MediaPicker = memo((props: ProductPhoto) => {
  const { productCategory } = props;
  const media = useSelector((state: RootState) => state.Product.media);
  const selectedMedia = useSelector(
    (state: RootState) => state.Product.selectedMedia
  );
  const productType = useSelector(
    (state: RootState) => state.Product.newProductType
  );

  const dispatch = useDispatch();

  const pickFromMedia = useTranslation("pick_from_media_label");

  useEffect(() => {
    //fixme: Api requests made on viewport resizing.
    if (productType) GetMedia(dispatch, "product-photos", productType);
    //eslint-disable-next-line
  }, []);

  return (
    <Box sx={{ width: { xs: "92vw", sm: 560 }, maxWidth: "100%" }}>
      <SelfUpload />
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{
          mt: 1.5,
          mb: 1,
          pb: 0.8,
          borderBottom: "1px solid rgba(131, 60, 65, 0.18)",
        }}
      >
        <Typography
          sx={{
            color: "#7a6560",
            fontFamily:
              '"DM Mono", "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace',
            fontSize: "10px",
            letterSpacing: "0.1em",
            textTransform: "uppercase",
          }}
        >
          {pickFromMedia}
        </Typography>
        <Typography sx={{ color: "#7a6560", fontSize: "11px" }}>
          {media.length} assets
        </Typography>
      </Stack>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "repeat(2, minmax(0, 1fr))",
            sm: "repeat(3, minmax(0, 1fr))",
          },
          gap: 1,
          p: 0.25,
          maxHeight: 390,
          overflowY: "auto",
          scrollbarWidth: "thin",
        }}
      >
        {sortByCategory(media, productCategory?.name).map((photo) => (
          <MediaCard
            key={photo.id}
            media={photo}
            selected={selectedMedia.id === photo.id}
          />
        ))}
      </Box>
    </Box>
  );
});

export default MediaPicker;
